import { useEffect, useRef } from "react";
import * as pbi from "powerbi-client";
import PropTypes from "prop-types";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";

// Create a service instance using the Service class
// pbi.service is a module with a Service constructor, not an instance
const powerbi = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

/**
 * Performs the actual PowerBI embed operation
 * Extracted to reduce function nesting depth
 */
const performEmbed = async ({
  containerRef,
  embedInstanceRef,
  embedConfig,
  eventHandlers,
  getEmbeddedComponent,
  uniqueId,
  embedConfigHashRef,
}) => {
  // Reset the old instance if exists
  if (embedInstanceRef.current && containerRef.current) {
    try {
      powerbi.reset(containerRef.current);
      embedInstanceRef.current = null;
    } catch (error) {
      getLogger().error("PowerBIEmbedOptimized", {
        type: "Error",
        page_url: window.location.href,
        error_desc: "Error resetting PowerBI before re-embed",
        error_stack: error?.stack ?? null,
      });
    }
  }

  try {
    // Call embed directly without bootstrap
    const embeddedReport = powerbi.embed(containerRef.current, embedConfig);

    embedInstanceRef.current = embeddedReport;

    // Apply event handlers
    if (eventHandlers && eventHandlers.size > 0) {
      eventHandlers.forEach((handler, eventName) => {
        embeddedReport.on(eventName, handler);
      });
    }

    // Notify parent component with the embedded report
    if (getEmbeddedComponent) {
      getEmbeddedComponent(embeddedReport);
    }
  } catch (error) {
    getLogger().error("PowerBIEmbedOptimized", {
      type: "Error",
      page_url: window.location.href,
      error_desc: "Error embedding report",
      error_stack: error?.stack ?? null,
    });
    embedConfigHashRef.current = null;
  }
};

/**
 * Optimized PowerBI Embed component with improved embed management.
 *
 * Performance optimizations:
 * 1. Prevents duplicate embeds using config hash comparison
 * 2. Handles React StrictMode double-invocation correctly
 * 3. Proper event handler registration and cleanup
 * 4. Supports multiple instances with different page configurations
 *
 * Reference: https://learn.microsoft.com/en-us/power-bi/developer/embedded/embedded-performance-best-practices
 */
const PowerBIEmbedOptimized = ({
  embedConfig,
  isEmbedded,
  cssClassName,
  getEmbeddedComponent,
  eventHandlers,
  uniqueId, // Unique identifier for this embed instance
}) => {
  const containerRef = useRef(null);
  const embedInstanceRef = useRef(null);
  const embedConfigHashRef = useRef(null);
  const previousHandlersRef = useRef(null); // Track previous handlers for cleanup

  // Single effect to handle both bootstrap and embed
  useEffect(() => {
    // Only proceed if we have the full embed config with token
    if (
      !containerRef.current ||
      !isEmbedded ||
      !embedConfig?.accessToken ||
      !embedConfig?.embedUrl
    ) {
      return;
    }

    // Create a hash of the embed config to detect changes
    const configHash = JSON.stringify({
      embedUrl: embedConfig.embedUrl,
      accessToken: embedConfig.accessToken?.substring(0, 20), // Only first 20 chars to avoid huge strings
      pageName: embedConfig.pageName, // Include page name to differentiate between pages
    });

    // Skip if already embedded with the same config
    if (embedConfigHashRef.current === configHash) {
      return;
    }

    embedConfigHashRef.current = configHash;

    performEmbed({
      containerRef,
      embedInstanceRef,
      embedConfig,
      eventHandlers,
      getEmbeddedComponent,
      uniqueId,
      embedConfigHashRef,
    });

    // No additional cleanup needed - handlers are managed in the separate effect below
  }, [isEmbedded, embedConfig, eventHandlers, getEmbeddedComponent, uniqueId]);

  // Update event handlers when they change
  useEffect(() => {
    if (!embedInstanceRef.current || !eventHandlers) {
      return;
    }

    // Remove old handlers first to prevent memory leaks
    if (previousHandlersRef.current) {
      previousHandlersRef.current.forEach((handler, eventName) => {
        embedInstanceRef.current?.off(eventName, handler);
      });
    }

    // Add new handlers
    eventHandlers.forEach((handler, eventName) => {
      embedInstanceRef.current.on(eventName, handler);
    });

    // Store current handlers as previous for next cleanup
    previousHandlersRef.current = new Map(eventHandlers);

    // Cleanup function to remove handlers when effect re-runs or component unmounts
    return () => {
      if (embedInstanceRef.current && eventHandlers) {
        eventHandlers.forEach((handler, eventName) => {
          embedInstanceRef.current?.off(eventName, handler);
        });
      }
    };
  }, [eventHandlers]);

  // Cleanup on unmount
  useEffect(() => {
    const container = containerRef.current;
    return () => {
      // Remove all event handlers before resetting
      if (embedInstanceRef.current && previousHandlersRef.current) {
        previousHandlersRef.current.forEach((handler, eventName) => {
          embedInstanceRef.current?.off(eventName, handler);
        });
      }

      // Reset the PowerBI embed instance
      if (embedInstanceRef.current && container) {
        try {
          powerbi.reset(container);
        } catch (error) {
          getLogger().error("PowerBIEmbedOptimized", {
            type: "Error",
            page_url: window.location.href,
            error_desc: "Error resetting PowerBI embed",
            error_stack: error?.stack ?? null,
          });
        }
      }

      // Reset all refs to allow re-embedding when component remounts
      embedConfigHashRef.current = null;
      embedInstanceRef.current = null;
      previousHandlersRef.current = null;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cssClassName}
      data-powerbi-id={uniqueId}
      data-testid={uniqueId ? `powerbi-embed-${uniqueId}` : "powerbi-embed"}
    />
  );
};

PowerBIEmbedOptimized.propTypes = {
  embedConfig: PropTypes.object,
  isEmbedded: PropTypes.bool,
  cssClassName: PropTypes.string,
  getEmbeddedComponent: PropTypes.func,
  eventHandlers: PropTypes.instanceOf(Map),
  uniqueId: PropTypes.string,
};

export default PowerBIEmbedOptimized;
